import {
  faAngleDown,
  faAngleRight,
  faArrowLeft,
  faBarsFilter,
} from '@fortawesome/pro-regular-svg-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Chip } from 'src/components/Chip';
import { Divider } from 'src/components/Divider';
import { DropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { TextField, TextFieldProps } from 'src/components/TextField';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { isEmpty } from 'src/utils/typeCheck';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import { BooleanFilter } from './BooleanFilter';
import { ClientCustomStatusFilter } from './ClientCustomStatusFilter';
import { CustomProfileFieldFilter } from './CustomProfileFieldFilter';
import { DateRangeFilter } from './DateRangeFilter';
import { DifficultyLevelFilter } from './DifficultyLevelFilter';
import { ForceTypeFilter } from './ForceTypeFilter';
import { GroupFilter } from './GroupFilter';
import { LifecycleStageFilter } from './LifecycleStageFilter';
import { MuscleGroupFilter } from './MuscleGroupFilter';
import { SelectFilter } from './SelectFilter';
import { TagsFilter } from './TagsFilter';
import { TrainerFilter } from './TrainerFilter';
import { WorkoutPlanFilter } from './WorkoutPlanFilter';

interface FilterConfig {
  field: string;
  label: string;
  description?: string;
  hideFromToolbar?: boolean;
  required?: boolean;
  showBlank?: boolean;
  showDefaultOption?: boolean;
  disabled?: boolean;
  Component: React.ComponentType<{
    setValue: (newValue: any, close?: boolean) => void;
    value: any;
    field: any;
    label: string;
    valueOnly?: boolean;
    multiple?: boolean;
  }>;
  props?: {
    showList?: boolean;
    searchable?: boolean;
    options?: TextFieldProps['options'];
    showLabelPrefix?: boolean;
    multiple?: boolean;
    filterOptions?: (
      options: any[],
      { inputValue }: { inputValue: string }
    ) => any[];
    useUserId?: boolean;
  };
}

type useListSearchParamsReturn = ReturnType<typeof useListSearchParams>;

interface FiltersProps {
  filters: FilterConfig[];
  hideMainDropdown?: boolean;
  hideIndividualFilters?: boolean;
  sortableColumns?: SortableColumn[];
  params?: useListSearchParamsReturn['params'];
  updateParam?: useListSearchParamsReturn['updateParam'];
  updateFilterParam: useListSearchParamsReturn['updateFilterParam'];
  getFilterParamValue: useListSearchParamsReturn['getFilterParamValue'];
  resetFilterParams: useListSearchParamsReturn['resetFilterParams'];
}

interface SortableColumn {
  id: string | number;
  label: string;
  sortable?: boolean;
}

export const Filters: React.FC<FiltersProps> = ({
  filters,
  hideMainDropdown,
  hideIndividualFilters = false,
  sortableColumns,
  params,
  updateParam,
  updateFilterParam,
  getFilterParamValue,
  resetFilterParams,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'filters.common' });
  // const {
  //   params,
  //   updateParam,
  //   updateFilterParam,
  //   getFilterParamValue,
  //   resetFilterParams,
  // } = useListSearchParams({ paramPrefix: prefix });

  const [mainDropdownOpen, setMainDropdownOpen] = useState(false);
  const [selectedMainDropdownFilter, setSelectedMainDropdownFilter] =
    useState<FilterConfig | null>(null);

  const [openSingleFilterDropdown, setOpenSingleFilterDropdown] = useState<
    FilterConfig['field'] | null
  >(null);

  const setQueryParamValue = updateFilterParam;

  const setValue = (filter: FilterConfig, newValue: any, close = false) => {
    setQueryParamValue(filter.field, newValue);
    if (close) {
      closeFilterDropdowns();
    }
  };

  const closeFilterDropdowns = () => {
    if (mainDropdownOpen) {
      closeMainDropdown();
    }
    if (openSingleFilterDropdown) {
      setOpenSingleFilterDropdown(null);
    }
  };

  const closeMainDropdown = () => {
    setMainDropdownOpen(false);
    setTimeout(() => {
      setSelectedMainDropdownFilter(null);
    }, 300);
  };

  const onClickMainReset = () => {
    setQueryParamValue('page', 1);

    if (selectedMainDropdownFilter) {
      return resetFilter(selectedMainDropdownFilter.field);
    }

    return resetFilterParams();
  };

  const resetFilter = (field) => {
    setQueryParamValue(field, null);
    setQueryParamValue('page', 1);
  };

  const updateSort = (colAndDirString) => {
    if (!updateParam) return;

    if (!colAndDirString || colAndDirString === ' ') {
      updateParam('sortBy', null);
      updateParam('sortDir', null);
      return;
    }
    const split = colAndDirString.split(' ');
    updateParam('sortBy', split[0]);
    updateParam('sortDir', split[1]);
  };

  const activeFilters = filters.filter(
    (f: any) => !isEmpty(getFilterParamValue(f.field))
  );

  const sortOptions = sortableColumns?.reduce(
    (acc, col) => {
      if (!col.sortable) {
        return acc;
      }
      return [
        ...acc,
        { label: `${col.label} (asc)`, value: `${col.id} asc` },
        { label: `${col.label} (desc)`, value: `${col.id} desc` },
      ];
    },
    [{ label: `Default`, value: null }] as {
      label: string;
      value: any;
    }[]
  );

  const sortValue =
    params?.sortBy && params?.sortDir
      ? params.sortBy + ' ' + params.sortDir
      : null;

  return (
    <>
      {!hideMainDropdown && (
        <DropdownMenu
          drawerOnMobile
          open={mainDropdownOpen}
          setOpen={(bool) => {
            if (!bool) {
              closeMainDropdown(); // reset to the first screen as well
              return;
            }
            setMainDropdownOpen(bool);
          }}
          closeOnContentClick={false}
          trigger={
            <Chip
              label={
                <Stack direction="row" alignItems="center">
                  <Icon size="small" icon={faBarsFilter} />
                  {activeFilters.length ? (
                    <Avatar
                      size="small"
                      sx={{
                        width: '22px',
                        height: '22px',
                        ml: '4px',
                        fontSize: '12px',
                        color: activeFilters.length && 'primary.contrastText',
                        bgcolor: activeFilters.length && 'primary.a10',
                      }}
                    >
                      {activeFilters.length}
                    </Avatar>
                  ) : (
                    <Box sx={{ ml: 1, mr: 0.5 }}>{t('filters')}</Box>
                  )}
                </Stack>
              }
              variant="outlined"
              active={!!activeFilters.length}
              sx={{ '.MuiChip-label': { px: 1 } }}
            />
          }
        >
          <Stack sx={{ minWidth: '310px', maxWidth: '500px' }}>
            {selectedMainDropdownFilter ? (
              <Box>
                <ListItem
                  icon={faArrowLeft}
                  primary={t('allFilters')}
                  onClick={() => setSelectedMainDropdownFilter(null)}
                  sx={{ py: 2, mb: 1 }}
                />
                <Box sx={{ px: 2 }}>
                  <selectedMainDropdownFilter.Component
                    {...selectedMainDropdownFilter}
                    setValue={(newValue, close) =>
                      setValue(selectedMainDropdownFilter, newValue, close)
                    }
                    value={getFilterParamValue(
                      selectedMainDropdownFilter.field
                    )}
                    valueOnly={false}
                    {...selectedMainDropdownFilter.props}
                  />
                </Box>
              </Box>
            ) : (
              <>
                {!!sortOptions?.length && (
                  <>
                    <Divider sx={{ opacity: 0.5 }} />
                    <Box sx={{ px: 2, py: 2 }}>
                      <TextField
                        label={t('sort')}
                        size="small"
                        onChange={(e) => updateSort(e.target.value)}
                        value={sortValue}
                        options={sortOptions}
                      ></TextField>
                    </Box>
                  </>
                )}
                <Divider sx={{ opacity: 0.5 }} />
                {filters.map((filter) => {
                  const value = getFilterParamValue(filter.field);
                  const isDisabled = filter.disabled;
                  const valueOption = (filter.props?.options || []).find(
                    (o) => o.value === value
                  );
                  return (
                    <Tooltip
                      key={filter.field}
                      title={filter.description}
                      placement="right"
                    >
                      <div>
                        <ListItem
                          disabled={isDisabled}
                          clickable
                          onClick={() => setSelectedMainDropdownFilter(filter)}
                          sx={{ py: 1, px: 2 }}
                          primary={
                            <Box>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ width: '100%', py: 1 }}
                              >
                                <Typography variant="subtitle2">
                                  {filter.label}
                                </Typography>
                                <Icon
                                  size="small"
                                  color="info"
                                  icon={faAngleRight}
                                />
                              </Stack>
                              {(!isEmpty(value) || filter.showBlank) && (
                                <Chip
                                  sx={{ mb: 1 }}
                                  {...(isEmpty(valueOption?.clearable) ||
                                  !!valueOption?.clearable
                                    ? {
                                        onDelete: () =>
                                          resetFilter(filter.field),
                                      }
                                    : {})}
                                  label={
                                    <filter.Component
                                      {...filter}
                                      setValue={(newValue, close) =>
                                        setValue(filter, newValue, close)
                                      }
                                      value={getFilterParamValue(filter.field)}
                                      valueOnly={true}
                                      {...filter.props}
                                    />
                                  }
                                />
                              )}
                            </Box>
                          }
                        />
                        <Divider sx={{ opacity: 0.5 }} />
                      </div>
                    </Tooltip>
                  );
                })}
              </>
            )}
            <Stack direction="row-reverse" spacing={2} sx={{ p: 2 }}>
              <Button onClick={onClickMainReset}>{t('reset')}</Button>
            </Stack>
          </Stack>
        </DropdownMenu>
      )}
      {!hideIndividualFilters &&
        filters.map((filter) => {
          const value = getFilterParamValue(filter.field);
          const valueOption = (filter.props?.options || []).find(
            (o) => o.value === value
          );
          const canClear =
            isEmpty(valueOption?.clearable) || !!valueOption?.clearable;
          if (filter.hideFromToolbar) {
            return null;
          }
          return (
            <DropdownMenu
              drawerOnMobile
              key={filter.field}
              closeOnContentClick={false}
              open={openSingleFilterDropdown === filter.field}
              setOpen={(bool) => {
                setOpenSingleFilterDropdown(bool ? filter.field : null);
              }}
              disabled={filter.disabled}
              trigger={
                <Tooltip title={filter.description}>
                  <Box onKeyDown={(e) => e.stopPropagation()}>
                    <Chip
                      disabled={filter.disabled}
                      color={filter.required && !value ? 'error' : 'secondary'}
                      active={!isEmpty(value)}
                      onDelete={
                        !isEmpty(value) && canClear
                          ? (e) => {
                              e.stopPropagation();
                              resetFilter(filter.field);
                            }
                          : undefined
                      }
                      label={
                        !isEmpty(value) || filter.showBlank ? (
                          <filter.Component
                            key={filter.field}
                            {...filter}
                            setValue={(newValue, close) =>
                              setValue(filter, newValue, close)
                            }
                            value={value}
                            valueOnly={true}
                            {...filter.props}
                          />
                        ) : (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Box
                              sx={{
                                maxWidth: '250px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {filter.label}
                            </Box>
                            <Icon
                              size="small"
                              sx={{
                                color: filter.required
                                  ? 'error'
                                  : 'text.secondary',
                              }}
                              icon={faAngleDown}
                            />
                          </Stack>
                        )
                      }
                      variant="outlined"
                      sx={{ maxWidth: '250px' }}
                    />
                  </Box>
                </Tooltip>
              }
            >
              <Box
                sx={{ minWidth: '310px', maxWidth: '500px', p: 2 }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <filter.Component
                  key={filter.field}
                  {...filter}
                  setValue={(newValue, close) =>
                    setValue(filter, newValue, close)
                  }
                  value={value}
                  valueOnly={false}
                  {...filter.props}
                />
                {filter.description && (
                  <Typography variant="caption">
                    {filter.description}
                  </Typography>
                )}
                <Stack direction="row-reverse" spacing={2} sx={{ mt: 2 }}>
                  <Button onClick={() => resetFilter(filter.field)}>
                    {t('reset')}
                  </Button>
                </Stack>
              </Box>
            </DropdownMenu>
          );
        })}
    </>
  );
};

export {
  BooleanFilter,
  ClientCustomStatusFilter,
  CustomProfileFieldFilter,
  DateRangeFilter,
  DifficultyLevelFilter,
  ForceTypeFilter,
  GroupFilter,
  LifecycleStageFilter,
  MuscleGroupFilter,
  SelectFilter,
  TagsFilter,
  TrainerFilter,
  WorkoutPlanFilter,
};
